/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2021-2023 (original work) Open Assessment Technologies SA
 */

import { routes } from '@/core/utils/consts';
import { match } from 'path-to-regexp';

/**
 * Dynamically imports the controller required for the received route. This function is sent to router.start()
 * from oat-sa/tao-core-app/src/core/router on application bootstrap, and it is called by its
 * router.dispatch() method to import and init the controller associated to a new route.
 * match is used to support templated path variables (e.g.: details/:id)
 *
 * @param {string} route - pathname to resolve
 * @returns {Promise} - Promise containing the imported module resolution
 */
export default function (route) {
    // Login route
    if (match(routes.home)(route)) {
        return import('@/controller/monitoring/monitoring');
    }
    // details route
    if (match(routes.details)(route)) {
        return import('@/controller/details/details');
    }
    // authorize route
    if (match(routes.authorize)(route)) {
        return import('@/controller/authorize/authorize');
    }
    // report route
    if (match(routes.report)(route)) {
        return import('@/controller/report/report');
    }
    // pause route
    if (match(routes.pause)(route)) {
        return import('@/controller/pause/pause');
    }
    // resume route
    if (match(routes.resume)(route)) {
        return import('@/controller/resume/resume');
    }
    // terminate route
    if (match(routes.terminate)(route)) {
        return import('@/controller/terminate/terminate');
    }
    // reopen route
    if (match(routes.reopen)(route)) {
        return import('@/controller/reopen/reopen');
    }
    // reset route
    if (match(routes.reset)(route)) {
        return import('@/controller/reset/reset');
    }
    // retry authorize route
    if (match(routes.retryAuthorize)(route)) {
        return import('@/controller/retryAuthorize/retryAuthorize');
    }
    // extend time route
    if (match(routes.extendTime)(route)) {
        return import('@/controller/extendTime/extendTime');
    }
    // reset extra time route
    if (match(routes.resetExtraTime)(route)) {
        return import('@/controller/resetExtraTime/resetExtraTime');
    }
    // retry acs(report) route
    if (match(routes.retryReport)(route)) {
        return import('@/controller/retryReport/retryReport');
    }
    // retry acs(pause) route
    if (match(routes.retryPause)(route)) {
        return import('@/controller/retryPause/retryPause');
    }
    // retry acs(resume) route
    if (match(routes.retryResume)(route)) {
        return import('@/controller/retryResume/retryResume');
    }
    // retry acs(terminate) route
    if (match(routes.retryTerminate)(route)) {
        return import('@/controller/retryTerminate/retryTerminate');
    }
    // retry acs(reopen) route
    if (match(routes.retryReopen)(route)) {
        return import('@/controller/retryReopen/retryReopen');
    }
    // retry acs(extendTime) route
    if (match(routes.retryExtendTime)(route)) {
        return import('@/controller/retryExtendTime/retryExtendTime');
    }
    // retry acs(reset) route
    if (match(routes.retryReset)(route)) {
        return import('@/controller/retryReset/retryReset');
    }

    // Default (error)
    return import('@/controller/error/error');
}
